import API from '@Services/apiAxios';

export interface InvalidFieldItem {
  field?: string;
  reason?: string;
  [key: string]: any;
}

export interface ExtractionResponse {
  jobId?: string;
  status?: string;
  documentType?: string;
  confidence?: number;
  invalidFields?: (string | InvalidFieldItem)[];
  error?: string | null;
  fields?: any;
  message?: string;
  state?: string;
  [key: string]: any;
}

export interface ExtractionPollingOptions {
  onStatusChange?: (status: string) => void;
  isCancelled?: () => boolean;
  registerTimer?: (timer: NodeJS.Timeout | null) => void;
  pollIntervalMs?: number;
}

/**
 * Safely unwrap extraction response object whether returned at root level
 * or wrapped inside response.data.data
 */
export const unwrapExtractionResponse = (raw: any): ExtractionResponse => {
  if (!raw) return {};
  if (raw.jobId || raw.status || raw.fields) {
    return raw;
  }
  if (raw.data && typeof raw.data === 'object') {
    if (raw.data.jobId || raw.data.status || raw.data.fields) {
      return {
        ...raw.data,
        error: raw.data.error || raw.error || null,
      };
    }
  }
  return raw;
};

/**
 * Trigger async document extraction with mode = "async"
 * POST /speedboxbot/extract
 */
export const extractDocument = async (
  s3Key: string,
  documentType = 'shipment',
  orgCode?: string,
  sessionId?: string,
): Promise<ExtractionResponse> => {
  const requestHeaders: Record<string, string> = {};
  if (orgCode) {
    requestHeaders['org_code'] = orgCode;
  }
  if (sessionId) {
    requestHeaders['sessionId'] = sessionId;
  }

  const response = await API.post<ExtractionResponse>(
    '/speedboxbot/extract',
    {
      documentType,
      s3Key,
      mode: 'async',
    },
    {
      headers: requestHeaders,
    },
  );
  return unwrapExtractionResponse(response.data);
};

/**
 * Poll single job status
 * GET /speedboxbot/extract/{jobId}
 */
export const getExtractionStatus = async (
  jobId: string,
  orgCode?: string,
  sessionId?: string,
): Promise<ExtractionResponse> => {
  const requestHeaders: Record<string, string> = {};
  if (orgCode) {
    requestHeaders['org_code'] = orgCode;
  }
  if (sessionId) {
    requestHeaders['sessionId'] = sessionId;
  }

  const { data } = await API.get<ExtractionResponse>(`/speedboxbot/extract/${jobId}`, {
    headers: requestHeaders,
  });
  return unwrapExtractionResponse(data);
};

/**
 * Poll extraction job until terminal status (completed, needs_review, failed)
 * Waits 10 seconds before initial poll and between consecutive polls.
 */
export const pollExtraction = async (
  initialResponse: ExtractionResponse,
  orgCode?: string,
  sessionId?: string,
  options: ExtractionPollingOptions = {},
): Promise<ExtractionResponse> => {
  const { onStatusChange, isCancelled, registerTimer, pollIntervalMs = 10000 } = options;

  if (isCancelled?.()) throw new Error('Extraction cancelled');

  // If already terminal (e.g. sync fallback response)
  const initialStatus = initialResponse.status;
  if (initialStatus === 'completed' || initialStatus === 'failed' || initialStatus === 'needs_review') {
    return initialResponse;
  }

  const jobId = initialResponse.jobId;
  if (!jobId) {
    if (initialResponse.fields && Object.keys(initialResponse.fields).length > 0) {
      return initialResponse;
    }
    return {
      status: 'failed',
      error: initialResponse.error || 'Failed to initialize extraction: missing jobId in response',
    };
  }

  let currentStatus = initialStatus || 'pending';

  while (currentStatus === 'pending' || currentStatus === 'processing') {
    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.(
      currentStatus === 'processing'
        ? 'AI is analyzing shipment details... (processing)'
        : 'AI extraction in progress... (waiting for results)',
    );

    // Wait 10 seconds before the poll without overlapping requests
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        registerTimer?.(null);
        if (isCancelled?.()) {
          reject(new Error('Extraction cancelled'));
        } else {
          resolve();
        }
      }, pollIntervalMs);
      registerTimer?.(timer);
    });

    if (isCancelled?.()) throw new Error('Extraction cancelled');

    onStatusChange?.('Checking extraction status...');
    const pollResult = await getExtractionStatus(jobId, orgCode, sessionId);

    currentStatus = pollResult.status || 'failed';

    if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'needs_review') {
      return pollResult;
    }
  }

  return {
    status: currentStatus,
    error: `Unexpected extraction status: ${currentStatus}`,
  };
};
